import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  // MenuItem,
  Grid,
  CircularProgress,
} from "@mui/material";
import { useFormik } from "formik";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import * as Yup from "yup";
import { createPayment, directPayment } from "../../../api/payment.api";
import { useNavigate } from "react-router-dom";
import { getCartCount } from "../../../api/cart.api";
import { useDispatch } from "react-redux";
import "./PayByForm.scss";
import { getUserBillingDetails } from "../../../api/order-summary.api";
import { East } from "@mui/icons-material";

const PaymentSchema = Yup.object().shape({
  // email: Yup.string()
  //   .email("Invalid email address")
  //   .required("Email is required"),
  // taxId: Yup.string().matches(/^\d+$/, "Only numbers allowed"),
  country: Yup.string().required("Country is required"),
  cardHolderName: Yup.string()
    .matches(/^[A-Za-z\s]+$/, "Only alphabets allowed")
    .min(1, "Min 1 characters required")
    .max(50, "Max 50 characters allowed")
    .required("Card Holder Name is required")
    .matches(
      /^[A-Za-z]+(?: [A-Za-z]+)*$/,
      "Only alphabets and single spaces are allowed",
    ),
});

const PayByForm: React.FC<any> = ({
  grandTotal,
  orderId,
  retryClientSecret,
}) => {
  const [paymentMethod, setPaymentMethod] = useState<
    "credit_card" | "debit_card"
  >("credit_card");
  const [cardComplete, setCardComplete] = useState({
    number: false,
    expiry: false,
    cvc: false,
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [cardErrors, setCardErrors] = useState({
    number: "",
    expiry: "",
    cvc: "",
  });

  const [cardTouched, setCardTouched] = useState({
    number: false,
    expiry: false,
    cvc: false,
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSelectPayment = (method: "credit_card" | "debit_card") => {
    setPaymentMethod(method);
  };

  const [billingData, setBillingData] = useState<any>();
  const [billingId, setBillingId] = useState<number>(0);

  const elementStyles = {
    style: {
      base: {
        fontSize: "16px",
        color: "#000",
        fontFamily: "Roboto, sans-serif",
        backgroundColor: "#ffffff",
        "::placeholder": {
          color: "#ADB4BB",
          fontWeight: 400,
        },
        ":-webkit-autofill": {
          color: "#000",
        },
      },
      invalid: {
        color: "red",
      },
    },
  };

  const stripe = useStripe();
  const elements = useElements();

  const fetchCartCount = async () => {
    try {
      const response = await getCartCount();
      const count = response.data?.data?.data?.totalCount ?? 0;
      dispatch({ type: "CART:SET_COUNT", payload: { count: count } });
    } catch (error) {
      console.error("ERROR IN FETCHING CART COUNT::", error);
    }
  };

  const fetchUserBillingDetails = async () => {
    try {
      const response = await getUserBillingDetails();

      if (response.data.success) {
        const billingData = response.data.data.data;
        setBillingId(billingData.id);
        if (billingData) {
          setBillingData(billingData);
        } else {
          setBillingData(null);
        }
      }
    } catch (error) {
      console.error("ERROR FETCHING USER BILLING DETAILS::", error);
    }
  };

  const handleSubmit = async () => {
    if (!stripe || !elements) return;

    setIsProcessing(true);

    const cardElement = elements.getElement(CardNumberElement);
    if (!cardElement) return;
    const paymentDirectBody = {
      orderId,
      amount: grandTotal,
      paymentType: paymentMethod,
    };

    try {
      setIsProcessing(true);

      let clientSecret;
      let txnId;

      // If retryClientSecret exists
      if (retryClientSecret) {
        clientSecret = retryClientSecret;
      } else {
        // else create a new PaymentIntent
        const response = await directPayment(paymentDirectBody);
        txnId = response?.data?.data?.data?.txnId;
        clientSecret = response?.data?.data?.data?.clientSecret;
      }

      if (!clientSecret) {
        setIsProcessing(false);
        console.error("Client secret missing");
        return;
      }

      const cardElement = elements.getElement(CardNumberElement);
      if (!cardElement) return;

      // Create PaymentMethod
      const pmResult = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: formik.values.cardHolderName,
        },
      });

      if (pmResult.error) {
        console.error("PaymentMethod error:", pmResult.error);
        return;
      }

      // Confirm Payment with that PaymentMethod
      const paymentResult = await stripe.confirmCardPayment(clientSecret, {
        payment_method: pmResult.paymentMethod.id,
      });

      if (!txnId) {
        txnId = paymentResult?.paymentIntent?.id;
      }

      // last4 & brand
      const last4 = pmResult?.paymentMethod?.card?.last4;
      const brand = pmResult?.paymentMethod?.card?.brand;

      // FAILED PAYMENT
      if (paymentResult.error) {
        setIsProcessing(false);
        await fetchCartCount();

        const pi: any = paymentResult.paymentIntent;
        const charge = pi?.charges?.data?.[0];

        navigate("/payment-status", {
          state: {
            paymentStatus: false,
            details: {
              last4: paymentResult?.error?.payment_method?.card?.last4 || null,
              brand: paymentResult?.error?.payment_method?.card?.brand || null,
              paymentType: paymentMethod,
              transactionDate: pi?.created
                ? new Date(pi.created * 1000)
                : new Date(),
              status: pi?.status || "failed",
              grandTotal,
              orderId,
              txnId,
              errorMessage: paymentResult.error.message,
              errorCode: paymentResult.error.code,
              declineCode: paymentResult.error.decline_code,
              errorType: paymentResult.error.type,
            },
            paymentResult: paymentResult,
          },
        });

        return;
      }

      // SUCCESS PAYMENT

      if (paymentResult.paymentIntent?.status === "succeeded") {
        setIsProcessing(false);
        await fetchCartCount();

        const pi: any = paymentResult.paymentIntent;
        const charge = pi?.charges?.data?.[0];

        navigate("/payment-status", {
          state: {
            paymentStatus: true,
            details: {
              last4: last4,
              brand: brand,
              paymentType: paymentMethod,
              transactionDate: new Date(pi.created * 1000),
              status: pi.status,
              grandTotal,
              orderId,
              txnId,
            },
          },
        });
      }
    } catch (error) {
      setIsProcessing(false);
      console.error("Payment error:", error);
    }
  };

  const formik = useFormik({
    initialValues: {
      // email: "",
      // taxId: "",
      // country: "",
      cardHolderName: "",
    },
    validationSchema: PaymentSchema,
    onSubmit: handleSubmit,
  });

  const isPayEnabled =
    formik.values.cardHolderName.trim() !== "" &&
    cardComplete.number &&
    cardComplete.expiry &&
    cardComplete.cvc &&
    stripe &&
    elements;

  useEffect(() => {
    fetchUserBillingDetails();
  }, []);

  return (
    <Box className="payby">
      {/* Payment Method Selection */}
      <Box className="payby__methods">
        <Box className="payby__method">
          <Box
            className={`payby__method-box ${paymentMethod === "credit_card" ? "payby__method-box--active" : ""
              }`}
            onClick={() => handleSelectPayment("credit_card")}
          >
            <CreditCardIcon
              sx={{
                color: "#999999",
              }}
            />
            Credit Card
          </Box>
        </Box>

        <Box className="payby__method">
          <Box
            className={`payby__method-box ${paymentMethod === "debit_card" ? "payby__method-box--active" : ""
              }`}
            onClick={() => handleSelectPayment("debit_card")}
          >
            <CreditCardIcon
              sx={{
                color: "#999999",
              }}
            />
            Debit Card
          </Box>
        </Box>
      </Box>
      <Typography className="payby__heading">Card Details</Typography>

      {/* Payment Form */}
      {paymentMethod && (
        <form onSubmit={formik.handleSubmit} className="payby__form">
          <Grid container spacing={2}>
            {/* Card Holder Name */}
            <Grid item xs={12} sm={6}>
              <Typography className="payby__label">Card Holder Name</Typography>
              <TextField
                name="cardHolderName"
                placeholder="Enter Card Holder Name"
                fullWidth
                variant="outlined"
                value={formik.values.cardHolderName}
                onChange={(e) => {
                  const value = e.target.value
                    .replace(/[^A-Za-z\s]/g, "")
                    .replace(/\s{2,}/g, " ")
                    .slice(0, 50);
                  formik.setFieldValue("cardHolderName", value);
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.cardHolderName &&
                  !!formik.errors.cardHolderName
                }
                helperText={
                  formik.touched.cardHolderName && formik.errors.cardHolderName
                }
              />
            </Grid>

            {/* Card Number */}
            <Grid item xs={12} sm={6}>
              <Typography className="payby__label">Card Number</Typography>

              <Box className="stripe-input-wrapper">
                <CardNumberElement
                  options={{
                    ...elementStyles,
                    placeholder: "Enter 16 digit Card Number",
                  }}
                  onChange={(e) => {
                    setCardComplete((prev) => ({
                      ...prev,
                      number: e.complete,
                    }));
                    setCardErrors((prev) => ({
                      ...prev,
                      number: e.error ? e.error.message : "",
                    }));
                    setCardTouched((prev) => ({ ...prev, number: true }));
                  }}
                />
                {cardTouched.number && cardErrors.number && (
                  <Typography className="payby__error-text">
                    {cardErrors.number}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Expiry Date */}
            <Grid item xs={12} sm={6}>
              <Typography className="payby__label">
                Expiration Date (MM/YY)
              </Typography>

              <Box className="stripe-input-wrapper">
                <CardExpiryElement
                  options={{
                    ...elementStyles,
                    placeholder: "MM/YY",
                  }}
                  onChange={(e) => {
                    setCardComplete((prev) => ({
                      ...prev,
                      expiry: e.complete,
                    }));
                    setCardErrors((prev) => ({
                      ...prev,
                      expiry: e.error ? e.error.message : "",
                    }));
                    setCardTouched((prev) => ({ ...prev, expiry: true }));
                  }}
                />

                {cardTouched.expiry && cardErrors.expiry && (
                  <Typography className="payby__error-text">
                    {cardErrors.expiry}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* CVV */}

            <Grid item xs={12} sm={6}>
              <Typography className="payby__label">
                Security Code (CVV)
              </Typography>

              <Box className="stripe-input-wrapper">
                <CardCvcElement
                  options={{
                    ...elementStyles,
                    placeholder: "Enter CVV code",
                  }}
                  onChange={(e) =>
                    setCardComplete((prev) => ({ ...prev, cvc: e.complete }))
                  }
                />
                {cardTouched.cvc && cardErrors.cvc && (
                  <Typography className="payby__error-text">
                    {cardErrors.cvc}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Email */}
            {/* <Grid item xs={12} sm={6} md={3}>
              <Typography className="payby__label">Email Address</Typography>
              <TextField
                name="email"
                placeholder="Enter the email"
                fullWidth
                variant="outlined"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && !!formik.errors.email}
                // helperText={<ErrorMessage name="email" />}
                InputLabelProps={{ shrink: false }}
              />
            </Grid> */}

            {/* Tax ID */}
            {/* <Grid item xs={12} sm={6} md={3}>
              <Typography className="payby__label">
                Tax ID Number(optional)
              </Typography>
              <TextField
                name="taxId"
                placeholder="Enter Tax ID number"
                fullWidth
                variant="outlined"
                value={formik.values.taxId}
                // onChange={formik.handleChange}
                onChange={(e) => {
                  // Allow only digits and max 20 characters
                  const value = e.target.value.replace(/\D/g, "").slice(0, 20);
                  formik.setFieldValue("taxId", value);
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.taxId && !!formik.errors.taxId}
                // helperText={<ErrorMessage name="taxId" />}
              />
            </Grid> */}

            {/* Country */}
            {/* <Grid item xs={12} sm={6} md={3}>
              <Typography className="payby__label">Country</Typography>
              <TextField
                select
                name="country"
                fullWidth
                variant="outlined"
                value={formik.values.country}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                SelectProps={{
                  displayEmpty: true,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "43px",
                  },
                  "& .MuiSelect-select": {
                    padding: "10px 12px",
                    height: "43px",
                    display: "flex",
                    alignItems: "center",
                  },
                }}
              >
                <MenuItem value="" disabled>
                  -- Select Country --
                </MenuItem>
                <MenuItem value="USA">USA</MenuItem>
                <MenuItem value="India">India</MenuItem>
              </TextField>
            </Grid> */}
          </Grid>

          {/* Pay Button */}
          <Box className="payby__button-container">
            <Button
              type="submit"
              disabled={!isPayEnabled}
              className={`payby__pay-button ${!isPayEnabled
                ? "payby__pay-button--disabled"
                : "payby__pay-button--enabled"
                }`}
              onClick={handleSubmit}
              endIcon={<East />}
            >
              Pay $ {grandTotal}.00{" "}
            </Button>
          </Box>
        </form>
      )}

      {isProcessing && (
        <Box className="payby__loader-overlay">
          <Box className="payby__loader-box">
            {!successMessage && <CircularProgress color="inherit" />}

            <Typography className="payby__loader-text">
              {successMessage || "Processing your payment, please wait…"}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PayByForm;
